import { csvRow } from "@/lib/csv";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Search, Users, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

const PAGE_SIZE = 25;

const AdminCustomers = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data: orders, isLoading } = useQuery({
    queryKey: ["admin-all-orders-customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("orders").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const customers = useMemo(() => {
    if (!orders) return [];
    const map: Record<string, { email: string; name: string; orderCount: number; totalSpent: number; lastOrder: string }> = {};
    orders.forEach((o) => {
      const key = o.customer_email || o.user_id || "guest";
      if (!map[key]) {
        map[key] = { email: o.customer_email || "—", name: o.customer_name || "—", orderCount: 0, totalSpent: 0, lastOrder: o.created_at };
      }
      map[key].orderCount++;
      map[key].totalSpent += Number(o.total);
      if (o.created_at > map[key].lastOrder) map[key].lastOrder = o.created_at;
    });
    return Object.values(map).sort((a, b) => b.totalSpent - a.totalSpent);
  }, [orders]);

  const filtered = useMemo(() => {
    if (!search) return customers;
    const q = search.toLowerCase();
    return customers.filter((c) => c.email.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
  }, [customers, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const exportCsv = () => {
    const csv = ["Name,Email,Orders,Total Spent,Last Order", ...filtered.map((c) =>
      csvRow([c.name, c.email, c.orderCount, c.totalSpent.toFixed(2), format(new Date(c.lastOrder), "yyyy-MM-dd")]))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "customers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Customers</h1>
          <p className="text-sm text-muted-foreground font-body">{filtered.length} customers</p>
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
          <Download className="w-4 h-4 mr-1.5" />Export CSV
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search by name or email…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-center py-20">Loading…</div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Email</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Orders</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Total Spent</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden sm:table-cell">Last Order</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((c, i) => (
                  <tr key={i} className="border-b border-border hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3 font-body font-medium">{c.name}</td>
                    <td className="px-4 py-3 font-body text-muted-foreground">{c.email}</td>
                    <td className="px-4 py-3 font-body">{c.orderCount}</td>
                    <td className="px-4 py-3 font-body font-semibold">£{c.totalSpent.toFixed(2)}</td>
                    <td className="px-4 py-3 font-body text-muted-foreground hidden sm:table-cell">{format(new Date(c.lastOrder), "dd MMM yyyy")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground font-body">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />No customers found
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</Button>
                <Button size="sm" variant="ghost" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
